/**
 * CharacterCanvas — Character Lab on the shared SectionCanvas: all 5 levels
 * as one continuous scroll with the sticky rail, three-state depth dots,
 * milestone marks, and the depth line ("Levels 1–3 developed" — never a
 * percentage).
 *
 * Preserved from the wizard (CharacterLevel.js): the PR 19 autosave stack
 * (per-level diffing, content_rev threading, unload veto), role/arc/
 * antagonist selectors (Level 0), the enneagram block + arc helper
 * (Level 1, rendered AFTER the questions — writing first), per-level
 * completion with badges, rename/delete, AI Mentor + Stress Test with
 * metering-safe level attachment (level frozen at open, never fired by
 * scroll), and Character Web / workspace back-navigation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Brain, Check, CheckCircle2, Crosshair, Lightbulb, Pencil, Trash2, X, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import StoryAuditor from '../components/StoryAuditor';
import AIMentor from '../components/AIMentor';
import UpgradeModal from '../components/UpgradeModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import SaveStatusLabel from '../components/SaveStatusLabel';
import SectionCanvas from '../components/SectionCanvas';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { workspaceRoute } from '../lib/workspaceRoute';
import { createFormSave } from '../lib/formAutosave';
import { useFormAutosave } from '../hooks/useFormAutosave';
import { devLevel, groupDevLevel, depthSummary } from '../lib/devLevel';
import {
  LEVEL_CONFIG, ROLE_LEVEL_TITLES, ANTAGONIST_TYPES, SERIES_LEVEL_META,
  getQuestions, ENNEAGRAM_PROFILE, ENNEAGRAM_WINGS, ENNEAGRAM_VARIANTS,
  ARC_HELPER_KEYS,
} from '../lib/characterQuestions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = [0, 1, 2, 3, 4];
const focusStorageKey = (projectId) => `pangaji_canvas_focus_char_${projectId}`;
const sectionId = (lvl, key) => `l${lvl}-${key}`;

export default function CharacterCanvas() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({ 0: {}, 1: {}, 2: {}, 3: {}, 4: {} });
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null); // level number in flight
  const [characterProjectCount, setCharacterProjectCount] = useState(0);
  const [workspaceFormat, setWorkspaceFormat] = useState(null);
  const [showArcHelper, setShowArcHelper] = useState(false);
  const [showAuditor, setShowAuditor] = useState(false);
  const [showMentor, setShowMentor] = useState(false);
  const [showVoicesModal, setShowVoicesModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [focusId, setFocusId] = useState(() => {
    try {
      return localStorage.getItem(focusStorageKey(projectId)) || null;
    } catch {
      return null;
    }
  });
  // The level the scroll-spy last saw, and the level the mentor was OPENED
  // on. The mentor's currentLevel prop restarts its (metered) session when
  // it changes — so it gets the frozen open-time value, never the live spy.
  const levelInViewRef = useRef(0);
  const [mentorLevel, setMentorLevel] = useState(0);
  const saveClientRef = useRef(null);
  const lastSavedRef = useRef({ 0: null, 1: null, 2: null, 3: null, 4: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [userRes, projectRes, projectsRes] = await Promise.all([
          authFetch(`${API}/auth/me`, { credentials: 'include' }),
          authFetch(`${API}/projects/${projectId}`, { credentials: 'include' }),
          authFetch(`${API}/projects`, { credentials: 'include' }),
        ]);
        if (!projectRes.ok) throw new Error(`load failed (${projectRes.status})`);
        const userData = await userRes.json();
        const projectData = await projectRes.json();
        const projectsData = await projectsRes.json();
        if (cancelled) return;

        setUser(userData);
        setProject(projectData);
        setCharacterProjectCount(
          Array.isArray(projectsData)
            ? projectsData.filter((p) => p.project_type === 'character').length
            : 0,
        );
        if (projectData?.workspace_id && Array.isArray(projectsData)) {
          const ws = projectsData.find((p) => p.project_id === projectData.workspace_id);
          setWorkspaceFormat(ws?.format || null);
        }
        const levels = {};
        LEVELS.forEach((lvl) => {
          levels[lvl] = projectData[`level_${lvl}`] || {};
          lastSavedRef.current[lvl] = JSON.stringify(levels[lvl]);
        });
        setFormData(levels);
        // Re-key on project change; never regress a live client's rev.
        if (saveClientRef.current?.projectId !== projectId) {
          saveClientRef.current = {
            projectId,
            client: createFormSave({
              url: `${API}/projects/${projectId}`,
              initialRev: projectData.content_rev ?? null,
            }),
          };
        } else if (saveClientRef.current.client.getRev() == null) {
          saveClientRef.current.client.setRev(projectData.content_rev ?? null);
        }
      } catch (error) {
        console.error('Error loading character canvas:', error);
        if (!cancelled) {
          setProject(null);
          toast.error('Failed to load project');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Persist only the levels whose content moved (one edit = one PUT).
  const persistLevels = useCallback(
    async (data, meta) => {
      for (const lvl of LEVELS) {
        const json = JSON.stringify(data[lvl] || {});
        if (json === lastSavedRef.current[lvl]) continue;
        await saveClientRef.current.client.save(
          { level: lvl, data: data[lvl] || {}, completed: false },
          meta,
        );
        lastSavedRef.current[lvl] = json;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId],
  );
  const canRideUnload = useCallback(
    (data) =>
      LEVELS.filter((lvl) => JSON.stringify(data[lvl] || {}) !== lastSavedRef.current[lvl])
        .length <= 1,
    [],
  );
  const { status: saveStatus, markDirty, flush } = useFormAutosave({
    data: formData,
    save: persistLevels,
    enabled: !loading && !!project,
    canRideUnload,
  });

  const handleInputChange = (lvl, key, value) => {
    setFormData((prev) => ({ ...prev, [lvl]: { ...prev[lvl], [key]: value } }));
    markDirty();
  };

  const changeFocus = (idOrNull) => {
    setFocusId(idOrNull);
    try {
      if (idOrNull) localStorage.setItem(focusStorageKey(projectId), idOrNull);
      else localStorage.removeItem(focusStorageKey(projectId));
    } catch { /* storage blocked */ }
  };

  // ── project-attribute handlers (ported from the wizard verbatim) ──
  const startRename = () => { setNameInput(project?.title || ''); setEditingName(true); };
  const cancelRename = () => { setEditingName(false); setNameInput(''); };
  const saveRename = async () => {
    const title = nameInput.trim();
    if (!title || title === project?.title) { cancelRename(); return; }
    try {
      const res = await authFetch(`${API}/projects/${projectId}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error();
      setProject((prev) => (prev ? { ...prev, title } : prev));
      toast.success('Character renamed');
      cancelRename();
    } catch {
      toast.error('Failed to rename character');
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Character deleted');
      setShowDelete(false);
      navigate(
        project?.workspace_id
          ? workspaceRoute(project.workspace_id, workspaceFormat, 'characters')
          : '/dashboard?type=character',
      );
    } catch {
      toast.error('Failed to delete character');
    }
  };

  const putAttr = async (path, body, onOk, errMsg) => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}/${path}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (response.status === 403) {
        if (path === 'role') setShowVoicesModal(true);
        else toast.error('Upgrade to Pro to use the Enneagram');
        return;
      }
      if (!response.ok) throw new Error();
      const updated = await response.json();
      setProject(updated);
      onOk?.(updated);
    } catch (error) {
      console.error(`Error updating ${path}:`, error);
      toast.error(errMsg);
    }
  };
  const handleRoleChange = (role) =>
    putAttr('role', { character_role: role }, () => toast.success(`Role updated to ${role}`), 'Failed to update role');
  const handleArcTypeChange = (arcType) =>
    putAttr('arc-type', { arc_type: arcType }, () => toast.success(`Arc type updated to ${arcType}`), 'Failed to update arc type');
  const handleAntagonistTypeChange = (antagonistType) =>
    putAttr('antagonist-type', { antagonist_type: antagonistType }, () => toast.success('Antagonist type updated'), 'Failed to update antagonist type');

  const saveEnneagram = (patch) => {
    const payload = {
      enneagram_type: patch.enneagram_type ?? (project?.enneagram_type || ''),
      enneagram_wing: patch.enneagram_wing ?? (project?.enneagram_wing || ''),
      enneagram_variant: patch.enneagram_variant ?? (project?.enneagram_variant || ''),
    };
    if (!payload.enneagram_type) return;
    putAttr('enneagram', payload,
      () => toast.success('Enneagram updated — syncs to Plot Forge'),
      'Failed to update enneagram');
  };
  const handleEnneagramChange = (e) => {
    const type = e.target.value;
    if (!type) return;
    const keepWing = (ENNEAGRAM_WINGS[type] || []).includes(project?.enneagram_wing)
      ? project.enneagram_wing : '';
    saveEnneagram({ enneagram_type: type, enneagram_wing: keepWing });
  };

  const role = project?.character_role || 'protagonist';
  const isSeries = workspaceFormat === 'tv_series';
  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);
  const mentorUnlocked = characterProjectCount <= 1 || isIndiePlus;

  const questionsFor = useCallback(
    (lvl) => getQuestions(LEVEL_CONFIG[lvl], role, project?.arc_type, project?.antagonist_type, lvl, isSeries),
    [role, project?.arc_type, project?.antagonist_type, isSeries],
  );

  const handleCompleteLevel = async (lvl) => {
    const requiredQuestions = questionsFor(lvl).filter((q) => !q.optional);
    const allFilled = requiredQuestions.every((q) => (formData[lvl]?.[q.key] || '').trim());
    if (!allFilled) {
      toast.error('Please fill in all required fields before completing this level');
      return;
    }
    setCompleting(lvl);
    try {
      await flush();
      const client = saveClientRef.current?.client;
      const response = await authFetch(`${API}/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          level: lvl,
          data: formData[lvl] || {},
          completed: true,
          base_rev: client?.getRev() ?? null,
        }),
      });
      if (response.status === 403) { navigate('/upgrade'); return; }
      if (response.status === 409) { toast.error(t('saveStatus_conflict')); return; }
      if (!response.ok) throw new Error('Failed to complete level');
      const updated = await response.json();
      client?.setRev(updated.content_rev ?? null);
      setProject(updated);
      toast.success(`Level ${lvl} completed! Badge earned: ${LEVEL_CONFIG[lvl].badge}`);
    } catch (error) {
      console.error('Error completing level:', error);
      toast.error('Failed to complete level');
    } finally {
      setCompleting(null);
    }
  };

  // Deep link from the /character/:id/level/N shim (cold bookmark loads).
  const initialSectionId = useMemo(() => {
    const lvl = location.state?.scrollToLevel;
    if (lvl == null || !project) return null;
    const qs = questionsFor(lvl);
    return qs.length ? sectionId(lvl, qs[0].key) : null;
  }, [location.state, project, questionsFor]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground font-mono">Failed to load — reload the page.</div>
      </div>
    );
  }

  // ── Level 0 banner: role / arc / antagonist selectors ──
  const levelZeroBanner = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-border/40 pb-8 mb-8">
      <div className="space-y-3">
        <label htmlFor="character_role" className="block font-mono text-sm uppercase tracking-wider text-primary">
          {t('role')}
        </label>
        <select
          id="character_role"
          value={role}
          onChange={(e) => handleRoleChange(e.target.value)}
          data-testid="role-select"
          className="w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground"
        >
          <option value="protagonist">{t('protagonist')}</option>
          <option value="antagonist">{t('antagonist')}</option>
          <option value="supporting">{t('supporting')}</option>
        </select>
        <p className="text-xs text-muted-foreground">{t('char_roleDesc')}</p>
      </div>
      <div className="space-y-3">
        <label htmlFor="arc_type" className="block font-mono text-sm uppercase tracking-wider text-primary">
          {t('arcType')}
        </label>
        <select
          id="arc_type"
          value={project?.arc_type || 'positive'}
          onChange={(e) => handleArcTypeChange(e.target.value)}
          data-testid="arc-type-select"
          className="w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground"
        >
          <option value="positive">{t('changePositive')}</option>
          <option value="negative">{t('fallNegative')}</option>
          <option value="flat">{t('flat')}</option>
        </select>
        <p className="text-xs text-muted-foreground">{t('char_arcTypeDesc')}</p>
      </div>
      {role === 'antagonist' && (
        <div className="space-y-3 md:col-span-2">
          <label className="block font-mono text-sm uppercase tracking-wider text-primary">
            Opposition Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ANTAGONIST_TYPES.map((type) => {
              const isSelected = (project?.antagonist_type || 'person') === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleAntagonistTypeChange(type.value)}
                  className={`text-left p-4 border font-mono transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border/40 bg-background/50 text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  <div className="text-xs uppercase tracking-widest font-semibold mb-1">{type.label}</div>
                  <div className="text-xs leading-relaxed opacity-80">{type.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ── Level 1 footer: arc helper + enneagram (AFTER the questions) ──
  const arcKeys = ARC_HELPER_KEYS[project?.arc_type || 'positive'];
  const levelOneFooter = (
    <div className="space-y-6 border-t border-border/40 pt-6">
      {role === 'protagonist' && (
        <div>
          <Button
            onClick={() => setShowArcHelper((v) => !v)}
            data-testid="arc-helper-btn"
            variant="outline"
            className={`rounded-none font-mono uppercase tracking-widest text-xs px-4 py-2 ${showArcHelper ? 'bg-primary/20 border-primary' : ''}`}
          >
            <Lightbulb className="w-4 h-4 mr-2" />
            {t('arcHelper')}
          </Button>
          {showArcHelper && (
            <div className="mt-4 bg-card border border-border/40 p-6" data-testid="arc-helper-sidebar">
              <h3 className="font-mono text-lg font-semibold text-primary mb-2">{t(arcKeys.titleKey)}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t(arcKeys.descKey)}</p>
              <h4 className="font-mono text-xs uppercase text-primary mb-2">{t('keyElements')}</h4>
              <ul className="space-y-2 mb-4">
                {arcKeys.guidanceKeys.map((k, i) => (
                  <li key={i} className="text-xs text-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>{t(k)}
                  </li>
                ))}
              </ul>
              <h4 className="font-mono text-xs uppercase text-primary mb-2">{t('proTips')}</h4>
              <ul className="space-y-2">
                {arcKeys.tipsKeys.map((k, i) => (
                  <li key={i} className="text-xs text-muted-foreground italic flex items-start gap-2">
                    <span className="text-primary/60 mt-0.5">→</span>{t(k)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="enneagram" className="block font-mono text-sm uppercase tracking-wider text-primary">
            {t('char_enneagramLabel')} {!isIndiePlus && '(Indie+)'}
          </label>
          {!isIndiePlus && (
            <Button
              size="sm"
              onClick={() => navigate('/upgrade')}
              className="bg-primary/20 text-primary hover:bg-primary/30 rounded-none font-mono uppercase tracking-widest text-xs px-3 py-1"
            >
              {t('char_upgradeToIndie')}
            </Button>
          )}
        </div>
        <select
          id="enneagram"
          value={project?.enneagram_type || ''}
          onChange={handleEnneagramChange}
          disabled={!isIndiePlus}
          data-testid="enneagram-select"
          className={`w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground ${!isIndiePlus ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <option value="">{t('char_enneagramPh')}</option>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <option key={n} value={String(n)}>{t(`enn_type${n}`)}</option>
          ))}
        </select>

        {project?.enneagram_type && isIndiePlus && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label htmlFor="enneagram-wing" className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Wing</label>
              <select
                id="enneagram-wing"
                value={project?.enneagram_wing || ''}
                onChange={(e) => saveEnneagram({ enneagram_wing: e.target.value })}
                data-testid="enneagram-wing-select"
                className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:ring-2 focus:ring-primary"
              >
                <option value="">No wing</option>
                {(ENNEAGRAM_WINGS[project.enneagram_type] || []).map((w) => (
                  <option key={w} value={w}>{project.enneagram_type}w{w} — {ENNEAGRAM_PROFILE[w]?.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="enneagram-variant" className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Instinctual Variant</label>
              <select
                id="enneagram-variant"
                value={project?.enneagram_variant || ''}
                onChange={(e) => saveEnneagram({ enneagram_variant: e.target.value })}
                data-testid="enneagram-variant-select"
                className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:ring-2 focus:ring-primary"
              >
                <option value="">No subtype</option>
                {ENNEAGRAM_VARIANTS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {project?.enneagram_type && ENNEAGRAM_PROFILE[project.enneagram_type] && isIndiePlus && (
          <div className="mt-3 border border-primary/30 bg-primary/5 p-4 space-y-1.5" data-testid="enneagram-depth">
            <p className="font-mono text-xs uppercase tracking-wider text-primary">
              Type {project.enneagram_type}{project.enneagram_wing ? `w${project.enneagram_wing}` : ''} · {ENNEAGRAM_PROFILE[project.enneagram_type].name}
              {project.enneagram_variant ? ` · ${ENNEAGRAM_VARIANTS.find((v) => v.value === project.enneagram_variant)?.label}` : ''}
            </p>
            <p className="text-xs text-muted-foreground font-mono"><span className="text-foreground">Core desire:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].desire}</p>
            <p className="text-xs text-muted-foreground font-mono"><span className="text-foreground">Core fear:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].fear}</p>
            <p className="text-xs text-muted-foreground font-mono"><span className="text-green-400">In growth:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].growth}</p>
            <p className="text-xs text-muted-foreground font-mono"><span className="text-amber-500">Under stress:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].stress}</p>
          </div>
        )}
        {project?.enneagram_type && isIndiePlus && (
          <p className="text-xs text-muted-foreground mt-2">{t('char_enneagramSyncNote')}</p>
        )}
        {!isIndiePlus && (
          <p className="text-xs text-muted-foreground italic">{t('char_enneagramLockedNote')}</p>
        )}
      </div>
    </div>
  );

  const renderQuestion = (lvl, question) => ({ focused, onFocus }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={sectionId(lvl, question.key)}
          className="block font-mono text-sm uppercase tracking-wider text-foreground"
        >
          {question.label || t(question.labelKey)}
        </label>
        <button
          type="button"
          onClick={onFocus}
          data-testid={`focus-${sectionId(lvl, question.key)}`}
          title={focused ? 'Show all' : 'Focus on this question'}
          className={`flex-shrink-0 p-1 transition-colors ${
            focused ? 'text-primary' : 'text-muted-foreground/50 hover:text-foreground'
          }`}
        >
          <Crosshair className="w-3.5 h-3.5" />
        </button>
      </div>
      {question.short ? (
        <Input
          id={sectionId(lvl, question.key)}
          type="text"
          placeholder={question.placeholder || t(question.placeholderKey)}
          value={formData[lvl]?.[question.key] || ''}
          onChange={(e) => handleInputChange(lvl, question.key, e.target.value)}
          data-testid={`input-${question.key}`}
          className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50"
        />
      ) : (
        <AutoExpandTextarea
          id={sectionId(lvl, question.key)}
          placeholder={question.placeholder || t(question.placeholderKey)}
          value={formData[lvl]?.[question.key] || ''}
          onChange={(e) => handleInputChange(lvl, question.key, e.target.value)}
          data-testid={`input-${question.key}`}
          className={`bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 ${
            question.multiline ? 'min-h-[120px]' : 'min-h-[80px]'
          }`}
        />
      )}
    </div>
  );

  const groups = LEVELS.map((lvl) => {
    const cfg = LEVEL_CONFIG[lvl];
    const roleOverride = ROLE_LEVEL_TITLES[role]?.[lvl];
    const seriesMeta = isSeries ? SERIES_LEVEL_META[lvl] : null;
    const questions = questionsFor(lvl);
    const required = questions.filter((q) => !q.optional);
    const isCompleted = project?.badges?.includes(cfg.badge);
    return {
      id: `level-${lvl}`,
      title: `${lvl} · ${seriesMeta?.title || t(roleOverride?.titleKey || cfg.titleKey)}`,
      subtitle: seriesMeta?.subtitle || t(roleOverride?.subtitleKey || cfg.subtitleKey),
      banner: lvl === 0 ? levelZeroBanner : null,
      // Depth, not fill: dots and milestones judge substance. Short identity
      // fields (name, age) are judged as filled-or-not via devLevel's
      // progress band; required questions drive the group mark.
      dev: groupDevLevel(required.map((q) => formData[lvl]?.[q.key] || '')),
      milestoneText: t('milestone_developed', {
        name: seriesMeta?.title || t(roleOverride?.titleKey || cfg.titleKey),
      }),
      footer: (
        <div>
          {lvl === 1 ? levelOneFooter : null}
          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => handleCompleteLevel(lvl)}
              disabled={completing !== null}
              data-testid={`complete-level-${lvl}`}
              variant={isCompleted ? 'outline' : 'default'}
              className={`rounded-none font-mono uppercase tracking-widest text-xs px-5 py-2.5 ${
                isCompleted ? 'text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {completing === lvl ? t('saving') : isCompleted ? `Level ${lvl} ✓` : t('char_completeLevel', { n: lvl })}
            </Button>
          </div>
        </div>
      ),
      sections: questions.map((q) => ({
        id: sectionId(lvl, q.key),
        label: q.label || t(q.labelKey),
        dev: devLevel(formData[lvl]?.[q.key] || ''),
        render: renderQuestion(lvl, q),
      })),
    };
  });

  // "Levels 1–3 developed" — names, never a percentage.
  const developedLevels = depthSummary(
    LEVELS.filter((l) => l > 0).map((lvl) => ({
      label: String(lvl),
      texts: questionsFor(lvl).filter((q) => !q.optional).map((q) => formData[lvl]?.[q.key] || ''),
    })),
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(
              project?.workspace_id
                ? workspaceRoute(project.workspace_id, workspaceFormat, 'characters')
                : '/dashboard?type=character',
            )}
            data-testid="back-to-workspace-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs px-2 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">
              {project?.workspace_id ? 'Back to Workspace' : t('nav_backToDashboard')}
            </span>
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1">
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
                  className="h-8 w-40 sm:w-48 bg-background border-input rounded-none font-mono text-sm"
                  data-testid="rename-char-input"
                />
                <Button size="sm" onClick={saveRename} className="h-8 w-8 p-0 bg-primary text-primary-foreground rounded-none" aria-label="Save name" data-testid="save-char-name-btn">
                  <Check className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={cancelRename} className="h-8 w-8 p-0 rounded-none" aria-label="Cancel rename">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="text-sm font-mono truncate max-w-[40vw] sm:max-w-xs" data-testid="project-title">
                  {project?.title}
                </div>
                <Button variant="ghost" size="sm" onClick={startRename} className="h-7 w-7 p-0 flex-shrink-0" aria-label="Rename character" data-testid="rename-char-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDelete(true)} className="h-7 w-7 p-0 flex-shrink-0" aria-label="Delete character" data-testid="delete-char-btn">
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </>
            )}
          </div>
          {mentorUnlocked && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => { setMentorLevel(levelInViewRef.current); setShowMentor(true); }}
                data-testid="ai-mentor-btn"
                variant="outline"
                className="rounded-none font-mono uppercase tracking-widest text-xs px-3 sm:px-4 py-2 border-primary/50 text-primary hover:bg-primary/10"
              >
                <Brain className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('aiMentor')}</span>
              </Button>
              <Button
                onClick={() => setShowAuditor(true)}
                data-testid="stress-test-btn"
                variant="outline"
                className="rounded-none font-mono uppercase tracking-widest text-xs px-3 sm:px-4 py-2 border-primary/50 text-primary hover:bg-primary/10"
              >
                <Zap className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('stressTest')}</span>
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          {/* The depth line: which levels have real content — never a % */}
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="depth-line">
            {developedLevels
              ? t('char_depthDeveloped', {
                  levels: `${developedLevels.length === 1 ? 'Level' : 'Levels'} ${developedLevels.join(', ')}`,
                })
              : ' '}
          </span>
          <SaveStatusLabel status={saveStatus} />
        </div>

        <SectionCanvas
          groups={groups}
          focusId={focusId}
          onFocusChange={changeFocus}
          initialSectionId={initialSectionId}
          onActiveChange={(sid, groupId) => {
            const lvl = parseInt(String(groupId).replace('level-', ''), 10);
            if (!Number.isNaN(lvl)) levelInViewRef.current = lvl;
          }}
        />
      </main>

      {showAuditor && (
        <StoryAuditor
          projectId={projectId}
          projectType="character"
          isPro={mentorUnlocked}
          onClose={() => setShowAuditor(false)}
        />
      )}

      <AIMentor
        isOpen={showMentor}
        onClose={() => setShowMentor(false)}
        projectId={projectId}
        currentLevel={mentorLevel}
        isPro={mentorUnlocked}
      />

      {showVoicesModal && (
        <UpgradeModal
          title="Your story needs a full cast."
          description="You've built your Protagonist and Antagonist. Upgrade to add supporting characters, love interests, and more."
          benefits={[
            'Unlimited character projects',
            'Supporting characters & ensemble cast',
            'Full Character Web',
            'Cross-character personality syncing',
          ]}
          ctaText="Unlock Indie"
          onClose={() => setShowVoicesModal(false)}
        />
      )}

      <DeleteConfirmModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={confirmDelete}
        projectName={project?.title || ''}
      />
    </div>
  );
}
